import { Show, createEffect, onCleanup, createSignal, onMount } from "solid-js";
import type { JSX } from "solid-js";
import { classNames } from "../utils/class_names";
import { placeholderCardBig } from "./placeholder";
import styles from "./hubble_image.module.scss";

export type ImageProps = {
  src: string;
  alt: string;
  class: string;
  errorImage?: string;
  imageBorderStyleClass?: string;
  priority?: boolean;
  role?: JSX.AriaAttributes["role"];
  onLoad?: () => void;
};

export default function HubbleImage(props: ImageProps) {
  let img: HTMLImageElement | undefined;
  let div!: HTMLDivElement;

  const [imageLoaded, setImageLoaded] = createSignal(false);
  const [errorImage, setErrorImage] = createSignal(false);
  const [timeoutId, setTimeoutId] = createSignal<ReturnType<
    typeof setTimeout
  > | null>(null);

  const getAdaptiveTimeout = () => {
    if (typeof navigator !== "undefined" && "connection" in navigator) {
      const conn = (navigator as any).connection;
      if (conn?.effectiveType) {
        switch (conn.effectiveType) {
          case "slow-2g":
          case "2g":
            return 20000;
          case "3g":
            return 15000;
          default:
            return 10000;
        }
      }
    }
    return 10000;
  };

  createEffect(() => {
    const src = props.src;
    if (src) {
      setImageLoaded(false);
      setErrorImage(false);

      const existingTimeout = timeoutId();
      if (existingTimeout) {
        clearTimeout(existingTimeout);
        setTimeoutId(null);
      }
    }
  });

  onMount(() => {
    const handleLoad = () => {
      setImageLoaded(true);
      setErrorImage(false);
      props.onLoad?.();

      const timeout = timeoutId();
      if (timeout) {
        clearTimeout(timeout);
        setTimeoutId(null);
      }
    };

    const handleError = () => {
      setImageLoaded(true);
      setErrorImage(true);

      const timeout = timeoutId();
      if (timeout) {
        clearTimeout(timeout);
        setTimeoutId(null);
      }
    };

    if (img) {
      img.onload = handleLoad;
    }
    if (img) {
      img.onerror = handleError;
    }

    if (img && img.complete) {
      if (img.naturalWidth > 0 && img.naturalHeight > 0) {
        setImageLoaded(true);
        setErrorImage(false);
        props.onLoad?.();
      } else {
        setImageLoaded(true);
        setErrorImage(true);
      }
    } else {
      const timeout = setTimeout(() => {
        if (!imageLoaded()) {
          setImageLoaded(true);
          setErrorImage(true);
        }
      }, getAdaptiveTimeout());

      setTimeoutId(timeout);
    }
  });

  onCleanup(() => {
    const timeout = timeoutId();
    if (timeout) {
      clearTimeout(timeout);
      setTimeoutId(null);
    }
  });

  return (
    <div class={classNames("relative", props.class)}>
      <Show when={!errorImage() && props.src}>
        <img
          ref={img}
          src={props.src}
          alt={props.alt}
          loading={props.priority ? "eager" : "lazy"}
          role={props.role}
          decoding="async"
          class={`absolute left-0 top-0 ${props.class} ${
            imageLoaded() ? "opacity-100" : "opacity-10"
          } transition-opacity duration-300 ease-in-out ${props.imageBorderStyleClass ?? ""}`}
        />
      </Show>

      <div class={`absolute left-0 top-0 h-full w-full`}>
        <Show when={!imageLoaded()}>
          <div
            ref={div}
            class={`absolute left-0 top-0 h-full w-full ${styles.skeleton} ${props.class}`}
          />
        </Show>

        <Show when={errorImage()}>
          <img
            class={props.class}
            src={props.errorImage ?? placeholderCardBig}
            alt="Image load fail"
            loading="eager"
          />
        </Show>
      </div>
    </div>
  );
}
